/**
 * Interview Management Validation Functions and Security Checks
 * Provides comprehensive validation and security checks for interview operations
 */

/**
 * Utility Functions
 */

// Safely parse a date string and return a valid Date object or null
export const safeParseDate = (dateString) => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  return !isNaN(date.getTime()) ? date : null;
};

// Format a date safely for display
export const formatDateSafe = (dateString, fallback = 'TBD') => {
  const date = safeParseDate(dateString);
  return date ? date.toISOString().split('T')[0] : fallback;
};

// Format a time safely for display  
export const formatTimeSafe = (dateString, fallback = 'TBD') => {
  const date = safeParseDate(dateString);
  return date ? date.toTimeString().slice(0, 5) : fallback;
};

/**
 * Validation Functions
 */

// Validate create interview data
export const validateCreateInterview = (data) => {
  const errors = [];

  // Required fields validation
  if (!data.candidate_id) errors.push({ field: 'candidate_id', message: 'Candidate ID is required' });
  if (!data.interviewer_id) errors.push({ field: 'interviewer_id', message: 'Interviewer ID is required' });
  if (!data.date) errors.push({ field: 'date', message: 'Date is required' });
  if (!data.time) errors.push({ field: 'time', message: 'Time is required' });
  if (!data.type) errors.push({ field: 'type', message: 'Interview type is required' });

  // Date validation
  if (data.date) {
    const interviewDate = new Date(`${data.date}T${data.time || '00:00'}`);
    if (interviewDate <= new Date()) {
      errors.push({ field: 'date', message: 'Interview must be scheduled for a future date and time' });
    }
  }

  // Time format validation
  if (data.time && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(data.time)) {
    errors.push({ field: 'time', message: 'Time must be in HH:MM format' });
  }

  // Duration validation
  if (data.duration && (data.duration < 15 || data.duration > 240)) {
    errors.push({ field: 'duration', message: 'Duration must be between 15 and 240 minutes' });
  }

  // Type validation - only video is supported
  if (data.type && data.type !== 'video') {
    errors.push({ field: 'type', message: 'Only video interviews are supported' });
  }

  // Meeting link is required for video interviews
  if (!data.meeting_link) {
    errors.push({ field: 'meeting_link', message: 'Meeting link is required' });
  }

  // URL validation for meeting link
  if (data.meeting_link && !/^https?:\/\/.+/.test(data.meeting_link)) {
    errors.push({ field: 'meeting_link', message: 'Meeting link must be a valid URL' });
  }

  // Notes length validation
  if (data.notes && data.notes.length > 1000) {
    errors.push({ field: 'notes', message: 'Notes cannot exceed 1000 characters' });
  }

  return {
    success: errors.length === 0,
    data: errors.length === 0 ? data : null,
    errors: errors.length > 0 ? errors : null
  };
};

// Validate update interview data
export const validateUpdateInterview = (data) => {
  const errors = [];

  // ID validation
  if (!data.id) errors.push({ field: 'id', message: 'Interview ID is required' });

  // Optional date validation
  if (data.scheduled_at) {
    const scheduledDate = new Date(data.scheduled_at);
    if (scheduledDate <= new Date()) {
      errors.push({ field: 'scheduled_at', message: 'Interview must be scheduled for a future date and time' });
    }
  }

  // Optional duration validation
  if (data.duration && (data.duration < 15 || data.duration > 240)) {
    errors.push({ field: 'duration', message: 'Duration must be between 15 and 240 minutes' });
  }

  // Optional type validation
  if (data.type && !['video', 'phone', 'in-person'].includes(data.type)) {
    errors.push({ field: 'type', message: 'Invalid interview type' });
  }

  // Optional status validation
  if (data.status && !['scheduled', 'completed', 'cancelled', 'rescheduled'].includes(data.status)) {
    errors.push({ field: 'status', message: 'Invalid interview status' });
  }

  // Optional URL validation
  if (data.meeting_link && data.meeting_link !== '' && !/^https?:\/\/.+/.test(data.meeting_link)) {
    errors.push({ field: 'meeting_link', message: 'Meeting link must be a valid URL' });
  }

  // Optional phone validation
  if (data.phone_number && data.phone_number !== '' && !/^[+]?[1-9][\d]{0,15}$/.test(data.phone_number)) {
    errors.push({ field: 'phone_number', message: 'Invalid phone number format' });
  }

  // Optional notes validation
  if (data.notes && data.notes.length > 1000) {
    errors.push({ field: 'notes', message: 'Notes cannot exceed 1000 characters' });
  }

  return {
    success: errors.length === 0,
    data: errors.length === 0 ? data : null,
    errors: errors.length > 0 ? errors : null
  };
};

// Validate feedback data
export const validateFeedback = (data) => {
  const errors = [];

  // Required fields
  if (!data.interview_id) errors.push({ field: 'interview_id', message: 'Interview ID is required' });
  if (!data.rating) errors.push({ field: 'rating', message: 'Rating is required' });
  if (!data.feedback) errors.push({ field: 'feedback', message: 'Feedback is required' });
  if (!data.recommendation) errors.push({ field: 'recommendation', message: 'Recommendation is required' });

  // Rating validation
  if (data.rating && (data.rating < 1 || data.rating > 5)) {
    errors.push({ field: 'rating', message: 'Rating must be between 1 and 5' });
  }

  // Feedback length validation
  if (data.feedback && (data.feedback.length < 10 || data.feedback.length > 2000)) {
    errors.push({ field: 'feedback', message: 'Feedback must be between 10 and 2000 characters' });
  }

  // Recommendation validation
  if (data.recommendation && !['strongly_recommend', 'recommend', 'neutral', 'do_not_recommend'].includes(data.recommendation)) {
    errors.push({ field: 'recommendation', message: 'Invalid recommendation value' });
  }

  // Optional score validations
  ['technical_score', 'communication_score', 'culture_fit_score'].forEach(field => {
    if (data[field] && (data[field] < 1 || data[field] > 10)) {
      errors.push({ field, message: `${field.replace('_', ' ')} must be between 1 and 10` });
    }
  });

  // Optional text field validations
  ['strengths', 'areas_for_improvement'].forEach(field => {
    if (data[field] && data[field].length > 1000) {
      errors.push({ field, message: `${field.replace('_', ' ')} cannot exceed 1000 characters` });
    }
  });

  return {
    success: errors.length === 0,
    data: errors.length === 0 ? data : null,
    errors: errors.length > 0 ? errors : null
  };
};

// Validate hiring decision data
export const validateHiringDecision = (data) => {
  const errors = [];

  // Required fields
  if (!data.interview_id) errors.push({ field: 'interview_id', message: 'Interview ID is required' });
  if (!data.hiring_status) errors.push({ field: 'hiring_status', message: 'Hiring status is required' });

  // Hiring status validation
  if (data.hiring_status && !['interviewed', 'under_consideration', 'hired', 'not_moving_forward'].includes(data.hiring_status)) {
    errors.push({ field: 'hiring_status', message: 'Invalid hiring status' });
  }

  // Optional field validations
  if (data.decision_notes && data.decision_notes.length > 1000) {
    errors.push({ field: 'decision_notes', message: 'Decision notes cannot exceed 1000 characters' });
  }

  if (data.offer_amount && data.offer_amount < 0) {
    errors.push({ field: 'offer_amount', message: 'Offer amount must be positive' });
  }

  if (data.start_date) {
    const startDate = new Date(data.start_date);
    if (startDate <= new Date()) {
      errors.push({ field: 'start_date', message: 'Start date must be in the future' });
    }
  }

  if (data.rejection_reason && data.rejection_reason.length > 500) {
    errors.push({ field: 'rejection_reason', message: 'Rejection reason cannot exceed 500 characters' });
  }

  return {
    success: errors.length === 0,
    data: errors.length === 0 ? data : null,
    errors: errors.length > 0 ? errors : null
  };
};

/**
 * Security validation functions
 */

// Validate user has permission to access interview
export const validateInterviewAccess = (interview, userId, userRole) => {
  // Admin can access all interviews
  if (userRole === 'admin') {
    return true;
  }
  
  // Employer can access interviews they created
  if (userRole === 'employer' && interview.interviewer_id === userId) {
    return true;
  }
  
  // Candidate can access their own interviews
  if (userRole === 'va' && interview.candidate_id === userId) {
    return true;
  }
  
  return false;
};

// Validate user can perform specific actions
export const validateInterviewAction = (action, interview, userId, userRole) => {
  // Check basic access first
  if (!validateInterviewAccess(interview, userId, userRole)) {
    return false;
  }
  
  switch (action) {
    case 'create':
      // Only employers can create interviews
      return userRole === 'employer';
      
    case 'update':
    case 'reschedule':
    case 'cancel':
      // Only the interviewer (employer) can modify
      return userRole === 'employer' && interview.interviewer_id === userId;
      
    case 'complete':
    case 'provide_feedback':
      // Only the interviewer can complete and provide feedback
      return userRole === 'employer' && interview.interviewer_id === userId;
      
    case 'view':
      // Both parties can view
      return true;
      
    case 'hiring_decision':
      // Only the interviewer (employer) can make hiring decisions
      return userRole === 'employer' && interview.interviewer_id === userId;
      
    default:
      return false;
  }
};

// Rate limiting configuration
export const RATE_LIMITS = {
  CREATE_INTERVIEW: { requests: 10, windowMs: 60000 }, // 10 per minute
  UPDATE_INTERVIEW: { requests: 20, windowMs: 60000 }, // 20 per minute
  FETCH_INTERVIEWS: { requests: 100, windowMs: 60000 } // 100 per minute
};

// Security headers for interview-related operations
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block'
};